import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'hooks/store';
import {
  clientResourcesItemsSelector,
  clientResourcesRemoteTotalSelector,
  isLoadingClientResourcesSelector,
  getClientResourceInventoriesListAction,
  cleanClientResourcesAction,
} from 'state/slices/clients';
import { usePagination } from 'hooks/pagination';
import {
  InventoryOwner,
  PaginationData,
  Resource,
  ResourceInventorySummary,
} from 'constants/interface';
import { Search, Table } from 'UI';
import { UnitMapping } from 'constants/unit';
import { useParams } from 'react-router';
import { ResourceCategoryUrlMapping } from 'constants/resource';

import {
  SearchContainer,
} from './style';

interface InventoryViewProps {
  shouldReload: boolean
  onReloadFinish(): void
}

interface ResourceInventoryRouteParams {
  categoryUrl: string
}

const InventoryView = ({
  shouldReload,
  onReloadFinish,
}: InventoryViewProps): JSX.Element => {
  const dispatch = useDispatch();
  const remoteTotal = useSelector(clientResourcesRemoteTotalSelector);

  const inventoryItems = useSelector(clientResourcesItemsSelector);
  const isRemoteLoading = useSelector(isLoadingClientResourcesSelector);

  const inventories = inventoryItems.map((inventory) => {
    const tableInventory: ResourceInventorySummary & {
      key?: string,
    } = {
      ...inventory,
      key: `${inventory.resource.id.toString()}/${inventory.owner.id.toString()}`,
    };
    return tableInventory;
  }).filter((tableInventory): tableInventory is ResourceInventorySummary => !!tableInventory);

  const {
    categoryUrl,
  } = useParams<ResourceInventoryRouteParams>();
  const activeCategory = ResourceCategoryUrlMapping[categoryUrl];

  const remoteFetch = (
    paginationData: PaginationData,
  ) => {
    const orderByMapping: Record<string, string> = {
      owner: 'client',
    };
    const orderBy = orderByMapping[paginationData.orderBy || ''] || paginationData.orderBy;
    dispatch(getClientResourceInventoriesListAction({
      ...paginationData,
      orderBy,
      category: activeCategory,
    }));
  };
  const pagination = usePagination<ResourceInventorySummary>(
    remoteFetch,
    {
      filter: {
        client: undefined,
        resource: undefined,
      },
    },
  );

  useEffect(() => {
    dispatch(cleanClientResourcesAction());
  }, []);

  useEffect(() => {
    pagination.updateTotal(remoteTotal);
  }, [remoteTotal]);

  useEffect(() => {
    if (shouldReload) {
      pagination.fetch();
      onReloadFinish();
    }
  }, [shouldReload]);

  const sorter = (): number => 0;

  const pageData = pagination.getInternal();

  return (
    <>
      <SearchContainer>
        <Search placeholder="Buscar no estoque" onSearch={pagination.onSearch} allowClear />
      </SearchContainer>
      <Table
        dataSource={inventories}
        pagination={pagination.getInternal()}
        loading={isRemoteLoading}
        showSorterTooltip={false}
        locale={{
          filterReset: 'Limpar',
          cancelSort: 'Clique para limpar ordenação',
          triggerAsc: 'Clique para ordem crescente',
          triggerDesc: 'Clique para ordem decrescente',
        }}
        onChange={pagination.onTableChange}
      >
        <Table.Column
          title="Cliente"
          key="client"
          dataIndex="owner"
          render={(
            owner: InventoryOwner,
          ) => owner.name}
          sorter={sorter}
          sortOrder={pageData.orderBy === 'owner' ? pageData.order : undefined}
        />
        <Table.Column
          title="Item"
          key="resource"
          dataIndex="resource"
          render={(resource: Omit<
            Resource,
            'createdAt' |
            'updatedAt' |
            'createdBy' |
            'updatedBy'
            >) => resource.name}
          sorter={sorter}
          sortOrder={pageData.orderBy === 'resource' ? pageData.order : undefined}
        />
        <Table.Column
          title="Saldo atual"
          key="quantity"
          dataIndex="quantity"
          render={(quantity: number, record: ResourceInventorySummary) => {
            const formattedQuantity = Intl
              .NumberFormat(
                'pt-BR',
              ).format(quantity);
            const content = `${formattedQuantity} ${UnitMapping[record.resource.unit].name}`;
            return content;
          }}
        />
      </Table>
    </>
  );
};

export default InventoryView;
